import { sendEvent } from '@/helpers/cerebro'

/**
 * 4-tier engagement events plugin.
 * All tiers fire once per session, run in parallel.
 * Compare in BigQuery to find the optimal bot-blocking gate.
 *
 * Tier 1: session_engaged           — 10s blind timer (baseline)
 * Tier 2: session_visible_engaged   — 5s accumulated visible time
 * Tier 3: session_strictly_engaged  — first click/touch/keypress
 * Tier 4: session_deeply_engaged    — interaction + 5s visible after it
 */
export function initPageTracking() {
  const pageLoadTime = Date.now()

  // ── Tier 1: session_engaged (10s blind timer, baseline) ──
  // Fires after 10s regardless of visibility or interaction.
  // Bots pass this easily — kept only as a comparison baseline.
  setTimeout(() => {
    sendEvent({
      eventName: 'session_engaged',
      eventGroup: 'engagement',
      properties: {
        engagement_duration: 10,
        page_visibility: document.visibilityState,
      },
    })
  }, 10000)

  // ── Tier 2: session_visible_engaged (5s accumulated visible time) ──
  // Tracks real visible time using visibilitychange API.
  // Tests whether visibility-gating alone filters bots.
  let visibleTimeMs = 0
  let visibleSince =
    document.visibilityState === 'visible' ? Date.now() : null
  let visibleFired = false

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden' && visibleSince) {
      visibleTimeMs += Date.now() - visibleSince
      visibleSince = null
    } else if (document.visibilityState === 'visible') {
      visibleSince = Date.now()
    }
  }

  document.addEventListener('visibilitychange', onVisibilityChange)

  const visibleInterval = setInterval(() => {
    if (visibleFired) return clearInterval(visibleInterval)

    let total = visibleTimeMs
    if (visibleSince) total += Date.now() - visibleSince
    if (total < 5000) return

    visibleFired = true
    clearInterval(visibleInterval)

    sendEvent({
      eventName: 'session_visible_engaged',
      eventGroup: 'engagement',
      properties: {
        visible_time_ms: Math.round(total),
        time_to_visible_engaged_ms: Date.now() - pageLoadTime,
        page_visibility: document.visibilityState,
      },
    })
  }, 1000)

  // ── Tier 3: session_strictly_engaged (interaction-only, no timer) ──
  // Fires on first real user interaction: click, touchstart, or keypress.
  // Scroll excluded — can auto-fire on mobile.
  // No time gate — interaction alone is the strongest bot separation signal.
  let strictlyFired = false

  ;['click', 'touchstart', 'keypress'].forEach((evt) =>
    document.addEventListener(
      evt,
      (e) => {
        if (strictlyFired) return
        strictlyFired = true

        sendEvent({
          eventName: 'session_strictly_engaged',
          eventGroup: 'engagement',
          properties: {
            first_interaction_type: e.type,
            time_to_interaction_ms: Date.now() - pageLoadTime,
            page_visibility: document.visibilityState,
          },
        })
      },
      { passive: true },
    ),
  )

  // ── Tier 4: session_deeply_engaged (interaction + 5s visible after) ──
  // Most strict tier. Requires a real interaction AND the user must remain
  // on the page with it visible for 5 more seconds after interacting.
  let deepInteractionType = null
  let deepInteractionTime = null
  let visibleAtInteraction = null
  let deeplyFired = false

  ;['click', 'touchstart', 'keypress'].forEach((evt) =>
    document.addEventListener(
      evt,
      () => {
        if (deepInteractionType) return
        deepInteractionType = evt
        deepInteractionTime = Date.now()
        // Snapshot visible time at interaction moment
        visibleAtInteraction = visibleTimeMs
        if (visibleSince) visibleAtInteraction += Date.now() - visibleSince
      },
      { passive: true },
    ),
  )

  const deepInterval = setInterval(() => {
    if (deeplyFired) return clearInterval(deepInterval)
    if (!deepInteractionTime) return

    // Total visible time now
    let totalVisible = visibleTimeMs
    if (visibleSince) totalVisible += Date.now() - visibleSince

    // Visible time after interaction = total now - snapshot at interaction
    const visibleAfter = totalVisible - visibleAtInteraction
    if (visibleAfter < 5000) return

    deeplyFired = true
    clearInterval(deepInterval)

    sendEvent({
      eventName: 'session_deeply_engaged',
      eventGroup: 'engagement',
      properties: {
        first_interaction_type: deepInteractionType,
        time_to_interaction_ms: deepInteractionTime - pageLoadTime,
        visible_time_after_interaction_ms: Math.round(visibleAfter),
        total_visible_time_ms: Math.round(totalVisible),
        page_visibility: document.visibilityState,
      },
    })
  }, 1000)
}
